import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { listClienti } from '../../services/clienti';
import { listTasks, createTask, updateTask, deleteTask, getTasksToAssume, claimTask, coordinatorDecision } from '../../services/tasks';
import { listAllProceduri } from '../../services/proceduri';
import { getAllDocuments } from '../../services/smartbill';
import { listStaffUsers } from '../../services/auth';
import TaskModal from '../../components/Shared/TaskModal';
import TaskAssumptionModal from '../../components/Shared/TaskAssumptionModal';
import InvoiceEditModal from '../../components/Shared/InvoiceEditModal';
import { getAlerts } from '../../services/alerts';
// buildAlerts no longer used here — the alert list now comes from GET /alerts/
// (main.rules.alerts.build_alerts, ported 1:1 and verified against real data,
// see Phase 0 in ai-agent-implementation-plan.md). getUrgency/SEV/TIP_LABELS
// stay: getUrgency is still used directly for task/document deadlines, which
// aren't part of the IP-procedure alert list this port covers.
import { getUrgency, SEV, TIP_LABELS } from '../../utils/alerts';
import './HomePage.css';

// Which route shows a given tip_proc — mirrors PROC_ROUTE_BY_TIP in the
// backend's main/reports.py (used to build the same links in the daily
// report email), so an alert opens the exact record everywhere consistently.
const PROC_ROUTE_BY_TIP = {
  marca_osim: '/oficii/osim', dmi: '/oficii/osim', brevet: '/oficii/osim',
  marca_euipo: '/oficii/euipo',
  marca_wipo: '/oficii/wipo',
  marca_internationala: '/oficii/internationala',
  idepot: '/oficii/benelux',
  dosar_instanta: '/litigii/dosare', opozitie_osim: '/litigii/dosare',
  opozitie_euipo: '/litigii/dosare', contestatie_osim: '/litigii/dosare',
  cai_atac_euipo: '/litigii/dosare', notificare_aviz: '/litigii/dosare',
  opozitii: '/litigii/dosare', contestatii: '/litigii/dosare',
  dosare_instanta: '/litigii/dosare', notificari_marketplace: '/litigii/dosare',
};

// Alerts carry procId/clientId/contractId/brandId directly from build_alerts()
// — prefer the specific procedure page over the generic client page whenever
// there's a matching route, same priority as the daily report email links.
function alertDestination(a) {
  const route = a.procId && PROC_ROUTE_BY_TIP[a.tip];
  if (route) return [route, { state: { openProcId: a.procId } }];
  return ['/clienti', { state: { openClientId: a.clientId, ...(a.cat === 'contract' ? { openTab: 'detalii' } : {}) } }];
}

// ── Date helpers ────────────────────────────────────────────
const WEEKDAYS = ['Duminică','Luni','Marți','Miercuri','Joi','Vineri','Sâmbătă'];
const MONTHS   = ['ianuarie','februarie','martie','aprilie','mai','iunie','iulie','august','septembrie','octombrie','noiembrie','decembrie'];
const MONTHS_S = ['ian','feb','mar','apr','mai','iun','iul','aug','sep','oct','nov','dec'];

function todayLabel() {
  const d = new Date();
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function daysDiff(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  return Math.round((d - today) / 86400000);
}
function fmtDateFull(s) {
  if (!s) return '';
  const d = new Date(s);
  return `${d.getDate()} ${MONTHS_S[d.getMonth()]} ${d.getFullYear()}`;
}
const IP_TIPS = ['marca_osim','marca_euipo','marca_wipo','marca_internationala','idepot','dmi','brevet'];

// ── Litigii events ──────────────────────────────────────────
function buildLitigiiEvents(dosare) {
  const today = new Date(); today.setHours(0,0,0,0);
  const events = [];
  for (const p of dosare) {
    const d = p.data || {};
    const faza = d.fazaCurenta || 'fond';
    const caseId = d.faze?.[faza]?.numarDosar || d.denumire || `#${p.id}`;
    for (const t of (d.termene || [])) {
      if (!t.data) continue;
      const dt = new Date(t.data); dt.setHours(0,0,0,0);
      if (dt < today) continue;
      events.push({ id:`t-${p.id}-${t.id||t.data}`, procId:p.id, date:t.data, type:'termen', label:t.obiectTermen||'', caseId, client:p.client_name||'' });
    }
    for (const s of (d.sedinte_sync?.sedinte || [])) {
      if (!s.data) continue;
      const dt = new Date(s.data); dt.setHours(0,0,0,0);
      if (dt < today) continue;
      events.push({ id:`s-${p.id}-${s.data}-${s.ora||''}`, procId:p.id, date:s.data, type:'sedinta', label:s.solutieSumar||s.solutie||'', caseId, client:p.client_name||'', ora:s.ora||'' });
    }
  }
  return events.sort((a,b) => a.date.localeCompare(b.date));
}


// ── Count-up hook ───────────────────────────────────────────
function useCountUp(target, active) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (target === 0) { setVal(0); return; }
    const dur = 900, start = Date.now();
    let raf;
    const tick = () => {
      const p = Math.min((Date.now()-start)/dur, 1);
      setVal(Math.round((1-Math.pow(1-p,3))*target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active]);
  return val;
}

// ── Animated hero canvas ────────────────────────────────────
function HeroCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf, t = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);
    const draw = () => {
      t += 0.003;
      const { width: w, height: h } = canvas;
      ctx.clearRect(0,0,w,h);
      const orbs = [
        { x:0.15+0.12*Math.sin(t),     y:0.5+0.35*Math.cos(t*.8),  r:.55, c:'rgba(99,102,241,.22)' },
        { x:0.78+0.08*Math.cos(t*1.2), y:0.3+0.4*Math.sin(t*.9),   r:.4,  c:'rgba(232,255,115,.13)' },
        { x:0.5+0.1*Math.sin(t*.6+1),  y:0.8+0.2*Math.cos(t*.5),   r:.35, c:'rgba(16,185,129,.12)' },
      ];
      for (const o of orbs) {
        const cx=w*o.x, cy=h*o.y, radius=w*o.r;
        const g = ctx.createRadialGradient(cx,cy,0,cx,cy,radius);
        g.addColorStop(0,o.c); g.addColorStop(1,'rgba(0,0,0,0)');
        ctx.fillStyle=g; ctx.fillRect(0,0,w,h);
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);
  return <canvas ref={ref} className="hp-hero-canvas" aria-hidden="true"/>;
}

// ── Stat card ───────────────────────────────────────────────
const HEX_RGB = { '#6366f1':'99,102,241','#10b981':'16,185,129','#f97316':'249,115,22','#dc2626':'220,38,38','#a78bfa':'167,139,250' };
function StatCard({ icon, value, label, color, delay, ready }) {
  const animated = useCountUp(value, ready);
  const rgb = HEX_RGB[color] || '99,102,241';
  return (
    <div className="hp-stat" style={{ '--sc':color, '--sc-bg':`rgba(${rgb},.12)`, animationDelay:delay }}>
      <div className="hp-stat-icon-wrap">{icon}</div>
      <div className="hp-stat-value">{ready ? animated : <span className="hp-stat-dash">—</span>}</div>
      <div className="hp-stat-label">{label}</div>
    </div>
  );
}

// ── Alert row ───────────────────────────────────────────────
function AlertRow({ alert, onClick }) {
  const s = SEV[alert.sev] || SEV.verde;
  // alert.urgency comes from GET /alerts/ (computed server-side, same
  // getUrgency logic, same color scale as tasks) — see Phase 0 in
  // ai-agent-implementation-plan.md.
  const u = alert.urgency || getUrgency(alert.date);
  return (
    <div className="hp-alert-row" style={{ '--ab': s.border, '--adot': s.dot }} onClick={onClick}>
      <div className="hp-alert-dot"/>
      <div className="hp-alert-body">
        <div className="hp-alert-main">
          <span className="hp-alert-label">{alert.label}</span>
          <div className="hp-alert-tags">
            {alert.date && (
              <span className="hp-chip" style={{ color: u.color, background: u.bg }}>
                {u.label}
              </span>
            )}
            <span className="hp-alert-tip">{TIP_LABELS[alert.tip]||alert.tip}</span>
          </div>
        </div>
        {(alert.name||alert.client) && (
          <div className="hp-alert-sub">
            {alert.name && <span className="hp-alert-name">{alert.name}</span>}
            {alert.name && alert.client && <span className="hp-alert-sep">·</span>}
            {alert.client && <span className="hp-alert-client">{alert.client}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────
export default function HomePage() {
  const { user, viewMode } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading]               = useState(true);
  const [tasks, setTasks]                   = useState([]);
  const [clients, setClients]               = useState([]);
  const [dosare, setDosare]                 = useState([]);
  const [ipProc, setIpProc]                 = useState([]);
  const [facturi, setFacturi]               = useState([]);
  const [staffUsers, setStaffUsers]         = useState([]);
  const [allAlerts, setAllAlerts]           = useState([]);
  const [taskModal, setTaskModal]           = useState(null);
  const [taskSaving, setTaskSaving]         = useState(false);
  const [taskDeleting, setTaskDeleting]     = useState(false);
  const [entered, setEntered]               = useState(false);
  const [alertFilter, setAlertFilter]       = useState('toate');
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [taskFilter, setTaskFilter]         = useState('toate');
  const [editDoc, setEditDoc]               = useState(null);
  const [assumptionData, setAssumptionData] = useState(null);   // {claimable, escalated} | null
  const [showAssumptionModal, setShowAssumptionModal] = useState(false);

  const loadTasksToAssume = () => {
    getTasksToAssume()
      .then(data => {
        setAssumptionData(data);
        setShowAssumptionModal((data?.claimable?.length || 0) > 0 || (data?.escalated?.length || 0) > 0);
      })
      .catch(() => {});
  };

  useEffect(() => { loadTasksToAssume(); }, []);

  const handleClaimTask = (id) => claimTask(id).then(loadTasksToAssume);
  const handleCoordinatorDecision = (id, payload) => coordinatorDecision(id, payload).then(loadTasksToAssume);

  useEffect(() => {
    const from = new Date();
    from.setMonth(from.getMonth()-6);
    const fromDate = from.toISOString().split('T')[0];
    Promise.all([
      listClienti(),
      listTasks(),
      listAllProceduri(['dosar_instanta']),
      listAllProceduri(IP_TIPS),
      getAllDocuments({ data_from: fromDate }),
      listStaffUsers(),
      getAlerts().catch(() => []),
    ]).then(([cd,td,pd,ipd,fd,ud,al]) => {
      setClients(cd.clients||[]);
      setTasks(td.tasks||[]);
      setDosare(pd.proceduri||[]);
      setIpProc(ipd.proceduri||[]);
      setFacturi(fd.data?.documente||[]);
      setStaffUsers(ud.users||[]);
      setAllAlerts(Array.isArray(al) ? al : []);
    }).catch(()=>{}).finally(()=>{
      setLoading(false);
      setTimeout(()=>setEntered(true), 150);
    });
  }, [viewMode]); // eslint-disable-line

  const activeTasks = useMemo(() =>
    tasks
      .filter(t => t.status !== 'terminat' && t.status !== 'anulat')
      .map(t => ({...t, _u: getUrgency(t.termen_limita)}))
      .sort((a,b) => a._u.order - b._u.order || (a.termen_limita||'').localeCompare(b.termen_limita||'')),
  [tasks]);

  const visibleTasks = useMemo(() =>
    taskFilter === 'ale_mele'
      ? activeTasks.filter(t => Array.isArray(t.asignat_la) && t.asignat_la.includes(user?.id))
      : activeTasks,
  [activeTasks, taskFilter, user?.id]);

  const unpaidFacturi = useMemo(() =>
    facturi
      .filter(d =>
        d.tip === 'fiscala' &&
        d.data_emitere && d.data_scadenta &&
        (d.status_plata === 'neplatita' || d.status_plata === 'incasata_partial')
      )
      .map(d=>({...d, _u:getUrgency(d.data_scadenta)}))
      .sort((a,b)=>{
        if (!a.data_scadenta&&!b.data_scadenta) return 0;
        if (!a.data_scadenta) return 1; if (!b.data_scadenta) return -1;
        return a.data_scadenta.localeCompare(b.data_scadenta);
      }),
    [facturi]
  );

  const litigiiEvents = useMemo(()=>buildLitigiiEvents(dosare),[dosare]);
  // allAlerts now comes straight from GET /alerts/ (state, set in the loading
  // effect above) — see Phase 0 in ai-agent-implementation-plan.md. No more
  // client-side buildAlerts()/standaloneBrands/contractePending derivation.

  const visibleAlerts = useMemo(()=>
    alertFilter==='toate' ? allAlerts : allAlerts.filter(a=>a.cat===alertFilter),
    [allAlerts, alertFilter]
  );

  const worstSev = useMemo(()=>
    allAlerts.length ? allAlerts[0].sev : null, [allAlerts]
  );

  const urgentCount = useMemo(()=>{
    const tu = visibleTasks.filter(t=>{const n=daysDiff(t.termen_limita);return n!==null&&n>=0&&n<=7;}).length;
    const lu = litigiiEvents.filter(e=>{const n=daysDiff(e.date);return n!==null&&n<=7;}).length;
    return tu+lu;
  },[activeTasks,litigiiEvents]);

  const handleTaskSave = async (form) => {
    setTaskSaving(true);
    try {
      if (taskModal&&taskModal!=='new'&&taskModal.id) {
        const res = await updateTask(taskModal.id, form);
        setTasks(prev=>prev.map(t=>t.id===res.task.id?res.task:t));
      } else {
        const res = await createTask(form);
        setTasks(prev=>[res.task,...prev]);
      }
      setTaskModal(null);
    } catch{} finally { setTaskSaving(false); }
  };

  const handleTaskDelete = async () => {
    if (!taskModal||taskModal==='new') return;
    setTaskDeleting(true);
    try {
      await deleteTask(taskModal.id);
      setTasks(prev=>prev.filter(t=>t.id!==taskModal.id));
      setTaskModal(null);
    } catch{} finally { setTaskDeleting(false); }
  };

  const firstName = (user?.first_name||'').trim()||user?.username||'';
  const SHOW_N = 6;

  return (
    <main className="hp-main">

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="hp-hero">
        <HeroCanvas/>
        <div className="hp-hero-inner">
          <div>
            <h1 className="hp-greeting">
              Bună ziua{firstName?', ':'.'}
              {firstName&&<span className="hp-name">{firstName}</span>}
              {firstName&&'.'}
            </h1>
            <p className="hp-date">{todayLabel()}</p>
          </div>
          {!loading&&(
            <div className="hp-pills">
              {activeTasks.length>0&&<span className="hp-pill">{activeTasks.length} task-uri active</span>}
              {unpaidFacturi.length>0&&<span className="hp-pill">{unpaidFacturi.length} facturi neachitate</span>}
              {allAlerts.length>0&&<span className="hp-pill hp-pill-hi">{allAlerts.length} alerte IP</span>}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────── */}
      <div className="hp-body">

        {/* Stat cards */}
        <div className="hp-stats">
          <StatCard ready={entered} delay="0ms"   icon={<IcoUsers/>}   value={clients.length}       label="Clienți"            color="#6366f1"/>
          <StatCard ready={entered} delay="70ms"  icon={<IcoCheck/>}   value={activeTasks.length}   label="Task-uri active"    color="#10b981"/>
          <StatCard ready={entered} delay="140ms" icon={<IcoInvoice/>} value={unpaidFacturi.length} label="Facturi neachitate" color="#f97316"/>
          <StatCard ready={entered} delay="210ms" icon={<IcoAlert/>}   value={allAlerts.length}      label="Alerte IP active"   color="#dc2626"/>
        </div>

        {/* ── 8c: Alerte IP ─────────────────────────────── */}
        {!loading && allAlerts.length > 0 && (
          <div className="hp-card hp-card-anim hp-alerts-card" style={{ animationDelay:'40ms', '--aw': worstSev ? SEV[worstSev].border : 'rgba(220,38,38,.2)' }}>
            <div className="hp-card-header">
              <div className="hp-ch-left">
                <span className="hp-ch-title">Alerte IP</span>
                <span className="hp-ch-badge">{allAlerts.length}</span>
                <div className="hp-alert-filters">
                  {['toate','reinnoire','taxa','publicare','contract'].map(f=>(
                    <button key={f} className={`hp-filt${alertFilter===f?' hp-filt-on':''}`} onClick={()=>setAlertFilter(f)}>
                      {f==='toate'?'Toate':f==='reinnoire'?'Reinnoiri':f==='taxa'?'Taxe & plăți':f==='publicare'?'Publicare BOPI':'Contracte'}
                    </button>
                  ))}
                </div>
              </div>
              <span className="hp-alert-sev-badge" style={{ color:worstSev?SEV[worstSev].color:'inherit', background:worstSev?SEV[worstSev].bg:'transparent' }}>
                {worstSev?SEV[worstSev].label:''}
              </span>
            </div>
            <div className="hp-alerts-list">
              {(alertsExpanded ? visibleAlerts : visibleAlerts.slice(0,SHOW_N)).map(a=>(
                <AlertRow key={a.id} alert={a} onClick={()=>navigate(...alertDestination(a))}/>
              ))}
              {visibleAlerts.length > SHOW_N && (
                <button className="hp-show-more" onClick={()=>setAlertsExpanded(e=>!e)}>
                  {alertsExpanded ? 'Afișează mai puțin ↑' : `Afișează toate (${visibleAlerts.length}) ↓`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content grid */}
        <div className="hp-content">

          {/* Left column: Termene + Tasks */}
          <div className="hp-left">

            {/* Termene & ședințe */}
            <div className="hp-card hp-card-anim" style={{animationDelay:'80ms'}}>
              <div className="hp-card-header">
                <div className="hp-ch-left">
                  <span className="hp-ch-title">Termene & ședințe</span>
                  {!loading&&<span className="hp-ch-badge">{litigiiEvents.length}</span>}
                </div>
                <button className="hp-link-btn" onClick={()=>navigate('/litigii/dosare')}>Dosare</button>
              </div>
              <div className="hp-list" style={{maxHeight:'300px'}}>
                {loading ? <Skelly n={5}/> : litigiiEvents.length===0 ? (
                  <EmptyMsg text="Niciun termen în curând."/>
                ) : litigiiEvents.slice(0,30).map(e=>{
                  const n=daysDiff(e.date);
                  let dl=fmtDateFull(e.date), dc='#64748b';
                  if (n===0)      { dl='Azi';   dc='#dc2626'; }
                  else if (n===1) { dl='Mâine'; dc='#ca8a04'; }
                  else if (n<=3)  { dc='#dc2626'; }
                  else if (n<=7)  { dc='#ea580c'; }
                  else if (n<=30) { dc='#ca8a04'; }
                  else            { dc='#16a34a'; }
                  const isSed=e.type==='sedinta';
                  return (
                    <button key={e.id} className="hp-lit" onClick={()=>navigate('/litigii/dosare', { state:{ openProcId: e.procId } })}>
                      <div className="hp-lit-dot" style={{background:isSed?'#6366f1':'#10b981'}}/>
                      <div className="hp-lit-body">
                        <div className="hp-lit-top">
                          <span className="hp-lit-case">{e.caseId}</span>
                          <span className="hp-lit-date" style={{color:dc}}>{dl}{e.ora?` · ${e.ora}`:''}</span>
                        </div>
                        <div className="hp-lit-meta">
                          <span className="hp-lit-type">{isSed?'Ședință':'Termen'}</span>
                          {e.label&&<span className="hp-lit-lbl"> · {e.label}</span>}
                        </div>
                        {e.client&&<div className="hp-lit-client">{e.client}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Task-uri */}
            <div className="hp-card hp-card-anim" style={{animationDelay:'120ms'}}>
              <div className="hp-card-header">
                <div className="hp-ch-left">
                  <span className="hp-ch-title">Task-uri</span>
                  {!loading&&<span className="hp-ch-badge">{visibleTasks.length}</span>}
                  <div className="hp-alert-filters">
                    {[['toate','Toate'],['ale_mele','Ale mele']].map(([f,lbl])=>(
                      <button key={f} className={`hp-filt${taskFilter===f?' hp-filt-on':''}`} onClick={()=>setTaskFilter(f)}>{lbl}</button>
                    ))}
                  </div>
                </div>
                <div className="hp-ch-right">
                  <button className="hp-link-btn" onClick={()=>navigate('/calendar')}>Calendar</button>
                  <button className="hp-add-btn" onClick={()=>setTaskModal('new')} title="Task nou">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                </div>
              </div>
              <div className="hp-list" style={{maxHeight:'300px'}}>
                {loading ? <Skelly n={7}/> : visibleTasks.length===0 ? (
                  <EmptyMsg text="Niciun task activ. 🎉"/>
                ) : visibleTasks.map(t=>{
                  const u=t._u;
                  const who=Array.isArray(t.asignat_la_names)&&t.asignat_la_names.length?t.asignat_la_names.join(' · '):null;
                  return (
                    <button key={t.id} className="hp-task" style={{'--tc':u.color}} onClick={()=>setTaskModal(t)}>
                      <div className="hp-task-row">
                        <span className="hp-task-name">{t.nume}</span>
                        <span className="hp-chip" style={{color:u.color,background:u.bg}}>{u.label}</span>
                      </div>
                      {(who||t.status==='in_progres')&&(
                        <div className="hp-task-sub">
                          {who&&<span>{who}</span>}
                          {t.status==='in_progres'&&<span className="hp-prog-dot">În progres</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Right column: Facturi neachitate */}
          <div className="hp-card hp-card-anim" style={{animationDelay:'160ms'}}>
            <div className="hp-card-header">
              <div className="hp-ch-left">
                <span className="hp-ch-title">Facturi neachitate</span>
                {!loading&&<span className="hp-ch-badge">{unpaidFacturi.length}</span>}
              </div>
              <button className="hp-link-btn" onClick={()=>navigate('/facturare')}>Facturare</button>
            </div>
            <div className="hp-list" style={{maxHeight:'640px'}}>
              {loading ? <Skelly n={3}/> : unpaidFacturi.length===0 ? (
                <EmptyMsg text="Nicio factură neachitată."/>
              ) : unpaidFacturi.map(d=>{
                const u=d._u;
                return (
                  <button key={d.id} className="hp-fact" style={{'--tc':u.color}} onClick={()=>setEditDoc(d)}>
                    <div className="hp-fact-row">
                      <span className="hp-fact-client">{d.client_name}</span>
                      <span className="hp-chip" style={{color:u.color,background:u.bg}}>{u.label}</span>
                    </div>
                    <div className="hp-fact-meta">
                      <span className="hp-fact-nr">{d.tip==='proforma'?'Proformă':'Factură'} {d.serie}-{d.numar}</span>
                      <span className="hp-fact-total">{d.total?.toFixed(2)} {d.moneda}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      {taskModal&&(
        <TaskModal
          task={taskModal==='new'?null:taskModal}
          staffUsers={staffUsers}
          clienti={clients}
          allProceduri={[...dosare, ...ipProc]}
          onSave={handleTaskSave}
          onDelete={taskModal!=='new'?handleTaskDelete:undefined}
          onClose={()=>setTaskModal(null)}
          saving={taskSaving}
          deleting={taskDeleting}
        />
      )}

      {editDoc&&(
        <InvoiceEditModal
          doc={editDoc}
          onClose={()=>setEditDoc(null)}
          onSaved={updated=>setFacturi(fs=>fs.map(f=>f.id===updated.id?updated:f))}
          onConverted={newFiscala=>setFacturi(fs=>[newFiscala,...fs])}
          onConvert={null}
          onReEdit={null}
          showToast={()=>{}}
        />
      )}

      {showAssumptionModal && assumptionData && (
        <TaskAssumptionModal
          claimable={assumptionData.claimable || []}
          escalated={assumptionData.escalated || []}
          staffUsers={staffUsers}
          onClose={()=>setShowAssumptionModal(false)}
          onClaim={handleClaimTask}
          onCoordinatorDecision={handleCoordinatorDecision}
        />
      )}
    </main>
  );
}

// ── Helpers ─────────────────────────────────────────────────
function Skelly({ n=5 }) {
  const ws=[72,50,80,44,65,58,75];
  return (
    <div className="hp-skelly">
      {Array.from({length:n},(_,i)=>(
        <div key={i} className="hp-sk-row">
          <div className="hp-sk-line" style={{width:`${ws[i%ws.length]}%`}}/>
          <div className="hp-sk-line hp-sk-xs"/>
        </div>
      ))}
    </div>
  );
}
function EmptyMsg({ text }) { return <div className="hp-empty">{text}</div>; }

// ── Icons ────────────────────────────────────────────────────
function IcoUsers()   { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>; }
function IcoCheck()   { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>; }
function IcoInvoice() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>; }
function IcoAlert()   { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
